/**
 * What a client may record about a position.
 *
 * This is self-reported history for reflection. It is seeded into `tooth_status_options` so the
 * practitioner can edit the wording without a deploy — the shape lives here, the content ends up in
 * the database.
 *
 * Every label has been through the language pass: no clinical nouns, no imaging vocabulary, and the
 * chart conventions borrowed from practice have been rewritten in the client's own terms.
 */

export type StatusCategory =
  | 'primary_status'
  | 'filling_material'
  | 'filling_surfaces'
  | 'crown_onlay'
  | 'root_canal'
  | 'missing_removed'
  | 'sensation'
  | 'fracture';

export interface StatusOption {
  readonly value: string;
  readonly label: string;
  readonly helpText?: string;
  /** Selecting this clears every other option in its category — "not sure" cannot coexist. */
  readonly exclusive?: boolean;
}

export interface StatusGroup {
  readonly category: StatusCategory;
  readonly title: string;
  readonly note?: string;
  /** Which primary status reveals this group. Absent for the primary group itself. */
  readonly revealedBy?: readonly string[];
  readonly multiple: boolean;
  readonly options: readonly StatusOption[];
}

const NOT_SURE: StatusOption = { value: 'not_sure', label: 'Not sure', exclusive: true };

export const PRIMARY_STATUS: StatusGroup = {
  category: 'primary_status',
  title: 'What is known about this position',
  note: 'Select everything that applies. This is what you remember or have been told — nothing here needs to be exact.',
  multiple: true,
  options: [
    { value: 'no_known_history', label: 'Nothing known, nothing to record', exclusive: true },
    NOT_SURE,
    {
      value: 'never_developed',
      label: 'Never developed',
      helpText: 'The tooth was never present.',
    },
    { value: 'missing_reason_unknown', label: 'Missing — reason unknown' },
    { value: 'removed', label: 'Removed' },
    { value: 'unerupted', label: 'Unerupted or still under the gum' },
    { value: 'retained_baby_tooth', label: 'Baby tooth still in place' },
    { value: 'implant', label: 'Implant in this position' },
    { value: 'bridge', label: 'Part of a bridge' },
    { value: 'crown_onlay', label: 'Crown or onlay' },
    { value: 'veneer', label: 'Veneer' },
    { value: 'filling', label: 'Filling, restoration or inlay' },
    { value: 'root_canal', label: 'Root canal history' },
    { value: 'fracture', label: 'Chipped, cracked or broken' },
    { value: 'sensation', label: 'Sensitivity or discomfort' },
    { value: 'infection_history', label: 'History of infection or abscess' },
    { value: 'gum_or_bone_change', label: 'Gum recession or bone change around this position' },
    { value: 'orthodontic_movement', label: 'Moved by braces or aligners' },
    { value: 'injury', label: 'Injury to this position' },
    { value: 'other', label: 'Something else' },
  ],
};

export const DETAIL_GROUPS: readonly StatusGroup[] = [
  {
    category: 'filling_material',
    title: 'Filling, restoration or inlay — material',
    note: 'An inlay is similar to a filling, but it is usually ceramic, made outside the mouth and then bonded into place.',
    revealedBy: ['filling'],
    multiple: true,
    options: [
      { value: 'amalgam', label: 'Silver-coloured' },
      { value: 'composite', label: 'Tooth-coloured' },
      { value: 'ceramic_inlay', label: 'Ceramic inlay, made outside the mouth and bonded in' },
      { value: 'gold', label: 'Gold' },
      { value: 'other', label: 'Something else' },
      NOT_SURE,
    ],
  },
  {
    category: 'filling_surfaces',
    title: 'Filling, restoration or inlay — where on the tooth',
    revealedBy: ['filling'],
    multiple: true,
    options: [
      { value: 'toward_midline', label: 'The side facing the front of your mouth' },
      { value: 'away_from_midline', label: 'The side facing the back of your mouth' },
      { value: 'cheek_side', label: 'The cheek or lip side' },
      { value: 'tongue_side', label: 'The tongue or palate side' },
      { value: 'biting_surface', label: 'The biting surface or edge' },
      NOT_SURE,
    ],
  },
  {
    category: 'crown_onlay',
    title: 'Crown or onlay — detail',
    note: 'An onlay is a partial crown. It covers more of the tooth than a filling, but not always all of it.',
    revealedBy: ['crown_onlay'],
    multiple: true,
    options: [
      { value: 'full_crown', label: 'Full crown' },
      { value: 'onlay', label: 'Onlay or partial crown' },
      { value: 'after_root_canal', label: 'Placed after a root canal' },
      { value: 'without_root_canal', label: 'Placed without a root canal' },
      NOT_SURE,
    ],
  },
  {
    category: 'root_canal',
    title: 'Root canal — detail',
    revealedBy: ['root_canal'],
    multiple: true,
    options: [
      { value: 'with_crown', label: 'With a crown' },
      { value: 'without_crown', label: 'Without a crown' },
      { value: 'redone', label: 'Done more than once' },
      { value: 'root_tip_surgery', label: 'Surgery at the root tip' },
      NOT_SURE,
    ],
  },
  {
    category: 'missing_removed',
    title: 'Missing or removed — what you remember',
    revealedBy: ['removed', 'missing_reason_unknown', 'never_developed'],
    multiple: true,
    options: [
      { value: 'never_developed', label: 'It never developed' },
      { value: 'wisdom_tooth', label: 'It was a wisdom tooth' },
      { value: 'pain_or_infection', label: 'Removed because of discomfort or infection' },
      { value: 'orthodontic', label: 'Removed to make room for braces' },
      { value: 'injury', label: 'Removed after an injury' },
      { value: 'gum_related', label: 'Removed because of the gum around it' },
      { value: 'reason_unknown', label: 'I do not know why' },
      NOT_SURE,
    ],
  },
  {
    category: 'sensation',
    title: 'Sensitivity or discomfort — what you notice',
    note: 'What you notice or remember about this position. There is no right answer here.',
    revealedBy: ['sensation'],
    multiple: true,
    options: [
      { value: 'cold', label: 'Sensitive to cold' },
      { value: 'heat', label: 'Sensitive to heat' },
      { value: 'biting', label: 'Noticeable when biting' },
      { value: 'unprompted', label: 'Comes on by itself' },
      { value: 'dull', label: 'A dull ache' },
      { value: 'sharp', label: 'Sharp' },
      { value: 'comes_and_goes', label: 'Comes and goes' },
      { value: 'long_standing', label: 'Long-standing or recurring' },
      NOT_SURE,
    ],
  },
  {
    category: 'fracture',
    title: 'Chipped, cracked or broken — detail',
    revealedBy: ['fracture'],
    multiple: true,
    options: [
      { value: 'small_chip', label: 'A small chip' },
      { value: 'visible_crack', label: 'A visible crack' },
      { value: 'broken_corner', label: 'A broken corner or cusp' },
      { value: 'broke_in_past', label: 'It broke at some point in the past' },
      { value: 'after_injury', label: 'It happened during an injury' },
      NOT_SURE,
    ],
  },
];

export const STATUS_GROUPS: readonly StatusGroup[] = [PRIMARY_STATUS, ...DETAIL_GROUPS];

export const STATUS_GROUPS_BY_CATEGORY: ReadonlyMap<StatusCategory, StatusGroup> = new Map(
  STATUS_GROUPS.map((group) => [group.category, group]),
);

/** Which detail groups open, given the primary statuses a client has selected. */
export function revealedGroups(primaryStatuses: readonly string[]): StatusGroup[] {
  const selected = new Set(primaryStatuses);
  return DETAIL_GROUPS.filter((group) =>
    (group.revealedBy ?? []).some((trigger) => selected.has(trigger)),
  );
}

/**
 * Applying a selection: an exclusive option clears the rest of its category, and choosing anything
 * else clears the exclusive one. Kept here rather than in the UI so the API can validate identically.
 */
export function applySelection(
  category: StatusCategory,
  current: readonly string[],
  value: string,
  selected: boolean,
): string[] {
  const group = STATUS_GROUPS_BY_CATEGORY.get(category);
  if (!group) throw new Error(`Unknown status category: ${category}`);

  const option = group.options.find((candidate) => candidate.value === value);
  if (!option) throw new Error(`Unknown option "${value}" in category ${category}`);

  if (!selected) return current.filter((entry) => entry !== value);
  if (option.exclusive) return [value];

  const exclusiveValues = new Set(
    group.options.filter((candidate) => candidate.exclusive).map((candidate) => candidate.value),
  );

  const next = current.filter((entry) => !exclusiveValues.has(entry));
  return next.includes(value) ? next : [...next, value];
}
